import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';
import {
    approvePolicy,
    editPolicyApprovalComment,
    removePolicyApproval,
    PopulatedPolicy,
} from '../../../services/api/policyService';
import { PolicyApprovalCard } from './PolicyApprovalCard';
import ApprovalDialog, { ApprovalDialogMode } from './ApprovalDialog';
import RemoveApprovalDialog from './RemoveApprovalDialog';

interface PolicyApprovalActionsProps {
    policy: PopulatedPolicy;
    setPolicy: (policy: PopulatedPolicy) => void;
    canApprove: boolean;
}

export default function PolicyApprovalActions({
    policy,
    setPolicy,
    canApprove,
}: PolicyApprovalActionsProps) {
    const { t } = useTranslation();
    const { toast } = useToast();

    const [dialogMode, setDialogMode] = useState<ApprovalDialogMode>(null);
    const [comment, setComment] = useState('');
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const openApprove = () => {
        setComment('');
        setDialogMode('approve');
    };

    const openEditComment = () => {
        setComment(policy.approval?.comment ?? '');
        setDialogMode('edit');
    };

    const closeDialog = () => {
        setDialogMode(null);
        setComment('');
    };

    const submitDialog = async () => {
        if (!dialogMode) return;
        const trimmed = comment.trim();
        setIsSubmitting(true);
        try {
            const updated =
                dialogMode === 'approve'
                    ? await approvePolicy(policy._id, trimmed || undefined)
                    : await editPolicyApprovalComment(policy._id, trimmed);
            setPolicy(updated);
            toast({
                title:
                    dialogMode === 'approve'
                        ? t('departmentPolicies.toasts.policyApproved')
                        : t('departmentPolicies.toasts.approvalCommentUpdated'),
            });
            closeDialog();
        } catch (err: any) {
            toast({
                title:
                    dialogMode === 'approve'
                        ? t('departmentPolicies.toasts.failedToApprove')
                        : t('departmentPolicies.toasts.failedToUpdateComment'),
                description: err?.response?.data?.message,
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitRemove = async () => {
        try {
            const updated = await removePolicyApproval(policy._id);
            setPolicy(updated);
            toast({ title: t('departmentPolicies.toasts.approvalRemoved') });
        } catch (err: any) {
            toast({
                title: t('departmentPolicies.toasts.failedToRemoveApproval'),
                description: err?.response?.data?.message,
                variant: 'destructive',
            });
        } finally {
            setRemoveConfirmOpen(false);
        }
    };

    const isPending = policy.document_id?.status === 'pending';
    const isApproved = !!policy.approval;
    const isMine = !!policy.approval?.approvedBy.isMine;
    // Only the approver may modify their approval
    const canModify = canApprove && isMine;

    return (
        <>
            {isPending && canApprove && (
                <Button
                    size="sm"
                    className="w-full justify-center whitespace-normal h-auto min-h-9 py-1.5 text-center bg-approval text-white hover:bg-approval-hover"
                    onClick={openApprove}
                >
                    <Check className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                    {t('departmentPolicies.readPage.approve')}
                </Button>
            )}

            {isApproved && (
                <PolicyApprovalCard
                    approval={policy.approval!}
                    onEdit={canModify ? openEditComment : undefined}
                    onRemove={
                        canModify ? () => setRemoveConfirmOpen(true) : undefined
                    }
                />
            )}

            <ApprovalDialog
                mode={dialogMode}
                comment={comment}
                onCommentChange={setComment}
                isSubmitting={isSubmitting}
                onCancel={closeDialog}
                onSubmit={submitDialog}
            />

            <RemoveApprovalDialog
                open={removeConfirmOpen}
                onOpenChange={setRemoveConfirmOpen}
                onConfirm={submitRemove}
            />
        </>
    );
}
